import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Search, X, CheckCircle, XCircle, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { UsuarioContext } from "../../../../../providers/userContext";
import DataTable from "../../../../../components/Tables/DataTable";
import FilterDropdown from "../../../../../components/filtros/FilterDropdown";
import Empty from "../../../../../icons/empty.png";
import { getInstalaciones, exportarInstalacionesKae } from "../../../../../services";
import { formatoFechasEs } from "../../../../../functions/funciones";
import { descargarCertificadoKae } from "../../../../../functions/pdfs";
import { construirURLParamsFiltros, useRecogerURLParamsFiltros } from "../../../../../functions/parametrosURL/funcionesParamsURL";

const InstalacionesLegalia = () => {
  const { t } = useTranslation("common");
  const user = useContext(UsuarioContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { filtrosIniciales, filtroOrder } = useRecogerURLParamsFiltros();

  const [busqueda, setBusqueda] = useState(filtrosIniciales || {});
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [busquedaActiva, setBusquedaActiva] = useState(false);
  const [clearFilterActivo, setClearFilterActivo] = useState(false);
  const [data, setData] = useState({ meta: {}, rows: [] });

  const fetchData = useCallback(async ({ offset, limit, filtros = busqueda, order, orderBy } = {}) => {
    orderBy = orderBy || filtroOrder.ordenadoPor || "id";
    order = order || filtroOrder.orden || "ASC";

    construirURLParamsFiltros(navigate, offset || 1, limit || 50, filtros, order, orderBy);

    const payload = { filtros, params: { page: offset, limit, order, orderBy } };
    try {
      setLoading(true);
      const response = await getInstalaciones(payload);
      const { meta, data: rawData } = response;
      setData({ meta, rows: formatData(rawData) });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [busqueda, filtroOrder.ordenadoPor, filtroOrder.orden, navigate]);

  useEffect(() => {
    if (localStorage.getItem("hasReloaded") === "true") {
      fetchData();
      localStorage.setItem("hasReloaded", "false");
    }
  }, [location]);

  const formatData = (rows) =>
    rows.map((row) => ({
      id: row.id,
      ano: row.invoiceDate ? new Date(row.invoiceDate).getFullYear() : "",
      nif: row.propertie?.nif,
      comunidad: row.propertie?.razonSocial,
      direccion: row.propertie?.direccion,
      codigoPostal: row.propertie?.codigoPostal,
      municipio: row.propertie?.municipio || "",
      provincia: row.propertie?.provincia || "",
      invoiceDate: row.invoiceDate,
      tecnico: row.technician ? `${row.technician.nombre} ${row.technician.apellidos}` : "",
      fechaRealizada: row.fecha_instalacion || "",
      realizada: row.realizada,
      senales: row.productos?.reduce((sum, p) => sum + (p.cantidad || 0), 0) || 0,
    }));

  const filtrarDatos = (filtros) => {
    setBusqueda(filtros);
    setBusquedaActiva(true);
  };

  const handleChange = (name, value) => {
    const updated = { ...busqueda };
    if (name === "ano") {
      if (!value) {
        delete updated.fechaFacturaInicio;
        delete updated.fechaFacturaFin;
        delete updated.ano;
      } else {
        updated.ano = value;
        updated.fechaFacturaInicio = `${value}-01-01`;
        updated.fechaFacturaFin = `${value}-12-31`;
      }
    } else if (value === "" || value === null || value === undefined) {
      delete updated[name];
    } else {
      updated[name] = value;
    }
    filtrarDatos(updated);
  };

  const clearFilters = () => {
    setBusqueda({});
    setClearFilterActivo(true);
  };

  const exportarExcel = async () => {
    if (exportando) return;
    setExportando(true);
    try {
      const blobData = await exportarInstalacionesKae(busqueda || {});
      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObj;
      link.setAttribute("download", `instalaciones_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (error) {
      console.error(error);
      toast.error(t("excel.excelError"), { autoClose: 5000 });
    } finally {
      setExportando(false);
    }
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 10; y--) years.push({ value: y, label: String(y) });
    return [{ value: "", label: t("filters.all", { defaultValue: "Todos" }) }, ...years];
  }, [t]);

  const realizadaOptions = [
    { value: "", label: t("filters.all", { defaultValue: "Todos" }) },
    { value: "true", label: t("filters.yes", { defaultValue: "Sí" }) },
    { value: "false", label: t("filters.no", { defaultValue: "No" }) },
  ];

  const StatusBadge = ({ realizada }) =>
    realizada ? (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3.5 h-3.5" />
        {t("status.completed", { defaultValue: "Realizada" })}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-orange-100 text-orange-600">
        <XCircle className="w-3.5 h-3.5" />
        {t("status.pending", { defaultValue: "Pendiente" })}
      </span>
    );

  const headers = [
    {
      id: "comunidad",
      label: t("installations.columns.community"),
      overrideFunc: (val, row) => (
        <>
          <Link to={`/comunidades/${row.nif}`} className="font-medium text-gray-900 hover:underline">
            {val}
          </Link>
          <div className="text-xs text-gray-400 mt-0.5">
            {[row.municipio, row.codigoPostal].filter(Boolean).join(", ")}
          </div>
        </>
      ),
    },
    {
      id: "nif",
      label: t("installations.columns.nif"),
    },
    {
      id: "ano",
      label: t("installations.columns.year"),
    },
    {
      id: "invoiceDate",
      label: t("installations.columns.invoiceDate"),
      overrideFunc: (val) => (val ? formatoFechasEs(val) : "-"),
    },
    {
      id: "tecnico",
      label: t("installations.columns.technician"),
      overrideFunc: (val) => val || "-",
    },
    {
      id: "senales",
      label: t("installations.columns.signals"),
    },
    {
      id: "realizada",
      label: t("installations.columns.done"),
      noObjeto: true,
      overrideFunc: (_val, row) => <StatusBadge realizada={row.realizada} />,
    },
    {
      id: "certificado",
      label: t("installations.columns.certificate"),
      noObjeto: true,
      overrideFunc: (_val, row) =>
        row.realizada ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              descargarCertificadoKae({
                tecnicoNombre: row.tecnico,
                ubicacionFinca: [row.direccion, row.codigoPostal, row.municipio, row.provincia].filter(Boolean).join(", "),
                lugar: row.municipio || row.provincia || "",
                fecha: row.fechaRealizada,
                nifPropiedad: row.nif,
              });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            style={{ border: "1px solid #e5e7eb" }}
            title={t("installations.detail.downloadCertificate")}
          >
            <Download size={14} />
            {t("installations.detail.downloadCertificate")}
          </button>
        ) : (
          "-"
        ),
    },
  ];

  const mensaje = (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <img src={Empty} alt="Empty" style={{ width: 75 }} />
      <p className="text-base font-semibold text-gray-900">
        {t("installations.messages.noInstallations")}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-8xl mx-auto bg-white shadow-sm p-3 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">
          {t("installations.title")}
        </h1>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          <FilterDropdown
            label={t("installations.columns.year")}
            value={busqueda.ano || ""}
            options={yearOptions}
            onChange={(v) => handleChange("ano", v)}
          />
          <FilterDropdown
            label={t("installations.columns.done")}
            value={
              busqueda.realizada === true
                ? "true"
                : busqueda.realizada === false
                ? "false"
                : ""
            }
            options={realizadaOptions}
            onChange={(v) => handleChange("realizada", v === "" ? undefined : v === "true")}
          />

          {Object.keys(busqueda).length > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 py-1.5 px-3 text-sm rounded-lg text-red-500 hover:bg-red-50 cursor-pointer"
              style={{ border: "1px solid #e5e7eb" }}
            >
              <X className="w-3.5 h-3.5" />
              {t("actions.clear", { defaultValue: "Limpiar" })}
            </button>
          )}

          <div className="hidden sm:flex flex-1" />

          <button
            onClick={exportarExcel}
            disabled={exportando}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              exportando ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
            }`}
            style={{ border: "1px solid #e5e7eb" }}
          >
            {exportando ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 text-green-600" />
            )}
            {exportando ? t("excel.generatingList") : t("excel.generateList")}
          </button>

          <div className="relative flex items-center w-full sm:w-auto">
            <Search className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder={t("actions.search", { defaultValue: "Buscar" }) + "..."}
              value={busqueda.comunidad || ""}
              onChange={(e) => handleChange("comunidad", e.target.value)}
              className="pl-9 pr-4 py-1.5 text-sm rounded-lg bg-white text-gray-700 w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-blue-100"
              style={{ border: "1px solid #e5e7eb" }}
            />
          </div>
        </div>

        <DataTable
          variant="legalia"
          data={data.rows}
          meta={data.meta}
          headers={headers}
          isLoading={loading}
          fetcher={fetchData}
          filtros={busqueda}
          busquedaActiva={busquedaActiva}
          setBusquedaActiva={setBusquedaActiva}
          clearFilterActivo={clearFilterActivo}
          setClearFilterActivo={setClearFilterActivo}
          clicable={true}
          onClickRow={(row) => navigate(`/instalaciones/${row.id}`)}
          mensaje={mensaje}
        />
      </div>
    </div>
  );
};

export default InstalacionesLegalia;
